import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {Password} from './password.model';

@Component({
    selector: 'app-password',
    templateUrl: './password.component.html',
    styleUrls: ['./password.component.scss'],
    standalone: false
})
export class PasswordComponent implements OnChanges, OnInit {

  @Input() requestOld: boolean = false;
  @Input() markWrongPassword: boolean = false;
  @Output() passwordValidated: EventEmitter<Password> = new EventEmitter<Password>();

  passwordChangeForm: FormGroup;

  passwordStatus: string = "weak";
  progressColor: string = "is-danger";
  strengthScore: number = 0;
  progressValue: number = 0;

  constructor(
    private formBuilder: FormBuilder
  ) {
    this.passwordChangeForm = this.formBuilder.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, this.complexPasswordValidator()]],
      confirmPassword: ['', Validators.required]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  ngOnInit() {
    if (!this.requestOld) {
      this.passwordChangeForm.removeControl('oldPassword');
    }

    this.passwordChangeForm.get('newPassword')?.valueChanges.subscribe(async value => {
      if (!value) {
        this.strengthScore = 0;
        this.progressValue = 0;
        this.passwordStatus = 'weak';
        this.progressColor = 'is-danger';
        return;
      }

      try {
        const { default: zxcvbn } = await import('zxcvbn');
        const result = zxcvbn(value);
        this.strengthScore = result.score;

        this.progressValue = this.strengthScore === 0 ? 15 : this.strengthScore * 25;

        const statusMap = ['weak', 'weak', 'medium', 'strong', 'very-strong'];
        const colorMap = ['is-danger', 'is-danger', 'is-warning', 'is-success', 'is-success'];

        this.passwordStatus = statusMap[this.strengthScore];
        this.progressColor = colorMap[this.strengthScore];
      } catch (e) {
        console.error("Error on password calculation", e);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markWrongPassword'] && this.markWrongPassword) {
      if (this.passwordChangeForm.contains('oldPassword')) {
         this.passwordChangeForm.get('oldPassword')?.setErrors({ wrongPassword: true });
      }
    }
  }

  complexPasswordValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const val = control.value;
      if (!val) return null;

      const hasMinLength = val.length >= 12;
      const hasMaxLength = val.length <= 128;
      const hasUpper = /[A-Z]/.test(val);
      const hasLower = /[a-z]/.test(val);
      const hasNumber = /[0-9]/.test(val);
      const hasSpecial = /[^A-Za-z0-9]/.test(val);
      const hasRepeats = /(.)\1{2,}/.test(val);

      let hasSequence = false;
      for (let i = 0; i < val.length - 2; i++) {
        const c1 = val.charCodeAt(i), c2 = val.charCodeAt(i+1), c3 = val.charCodeAt(i+2);
        if ((c2 === c1 + 1 && c3 === c2 + 1) || (c2 === c1 - 1 && c3 === c2 - 1)) {
          hasSequence = true; break;
        }
      }

      const isValid = hasMinLength && hasMaxLength && hasUpper && hasLower &&
                      hasNumber && hasSpecial && !hasRepeats && !hasSequence;

      if (!isValid) return { passwordRequirements: true };
      return null;
    };
  }

  passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const newPass = group.get('newPassword')?.value;
    const confirmPass = group.get('confirmPassword')?.value;
    return newPass === confirmPass ? null : { passwordMismatch: true };
  }

  validate() {
    if (this.passwordChangeForm.valid && this.strengthScore >= 3) {
      this.passwordValidated.emit({
        oldPassword: this.passwordChangeForm.contains('oldPassword') ? this.passwordChangeForm.get('oldPassword')?.value : null,
        newPassword: this.passwordChangeForm.get('newPassword')?.value,
        confirmPassword: this.passwordChangeForm.get('confirmPassword')?.value,
      })
    }
  }
}
